import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, ResponsiveContainer } from 'recharts';
import { useChowaTheme } from '../config/chowaThemeContext';
import { commonStyles } from '../config/ChowaDashboardLayout';
import classNames from 'classnames';
import Card from '../config/ChowaCard';
import NoInlineCard from '../config/ChowaNoInlineCard';
import Table from '../config/ChowaTable';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { HiOutlineSearch, HiOutlineX } from 'react-icons/hi';

// Swiper CSS
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// カスタムツールチップ
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: 10, fontFamily: 'Segoe UI, Roboto, Arial, sans-serif' }}>
        <table>
          <tbody>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Series:</td>
              <td>{item.modelSeries && item.modelSeries !== '' ? item.modelSeries : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Model:</td>
              <td>{item.name && item.name !== '' ? item.name : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Unit Serial:</td>
              <td>{item.unitSerialNumber && item.unitSerialNumber !== '' ? item.unitSerialNumber : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Device ID:</td>
              <td>{item.deviceId && item.deviceId !== '' ? item.deviceId : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Last Update:</td>
              <td>{item.timestamp ? new Date(item.timestamp).toLocaleString('ja-JP') : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Operating Time:</td>
              <td>{item.hours !== undefined && item.hours !== null && item.hours !== '' ? item.hours + 'h' : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Working Days:</td>
              <td>{item.operationDays !== undefined && item.operationDays !== null && item.operationDays !== '' ? item.operationDays + '日' : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

const TimeEngineHoursBarChart = ({ data, seriesColor, selectedModel }) => {
  const { isDark, getCurrentTheme } = useChowaTheme();
  const currentTheme = getCurrentTheme();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleButtonIndices, setVisibleButtonIndices] = useState([0, 1, 2, 3, 4]); // 表示されているボタンのインデックス
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  // ページネーションの色（現在のテーマカラーを使用）
  const getPaginationColor = () => {
    return currentTheme.primary;
  };

  // チャートバーの色（propsのseriesColor優先、なければテーマカラー）
  const barColor = seriesColor || currentTheme.primary;

  // シリーズ色名を決定
  const getTableColorName = (color) => {
    if (!color) return 'gray';
    if (color.startsWith('#3b82f6')) return 'blue';
    if (color.startsWith('#f97316')) return 'orange';
    if (color.startsWith('#9333ea')) return 'purple';
    return 'gray';
  };
  const tableColor = getTableColorName(seriesColor);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // データが変更された時にvisibleButtonIndicesをリセット
  useEffect(() => {
    if (data && data.length > 5) {
      const step = 10;
      const totalButtons = Math.ceil(data.length / step) + 1; // +1は最初の「1」ボタンのため
      const maxButtons = Math.min(5, totalButtons);
      const initialIndices = Array.from({ length: maxButtons }, (_, i) => i);
      setVisibleButtonIndices(initialIndices);
    }
  }, [data]);

  // 検索でフィルタリングされたデータ
  const filteredData = data ? data.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // 検索クリア機能
  const clearSearch = () => {
    setSearchTerm('');
  };

  // データの最初のModel名から#番号を抽出
  const getStartNumber = () => {
    if (!data || data.length === 0) return 1;
    const match = data[0].name && data[0].name.match(/#(\d+)$/);
    return match ? parseInt(match[1], 10) : 1;
  };
  const startNumber = getStartNumber();

  // スキップボタンの生成（<< + 5個 + >>ボタン）
  const generateSkipButtons = () => {
    const buttons = [];
    const step = 10;
    
    // 検索中は表示しない
    if (searchTerm) return buttons;
    
    // データが5個以下の場合は表示しない
    if (!data || data.length <= 5) return buttons;
    
    // 利用可能なボタンの総数を計算
    const totalButtons = Math.ceil(data.length / step) + 1; // +1は最初のボタンのため
    
    // <<ボタンを追加（最初の位置でない場合）
    if (visibleButtonIndices[0] > 0) {
      buttons.push(
        <button
          key="prev"
          onClick={() => {
            const newIndices = [...visibleButtonIndices];
            const newFirstIndex = Math.max(0, newIndices[0] - 1);
            if (newFirstIndex < newIndices[0]) {
              newIndices.pop();
              newIndices.unshift(newFirstIndex);
              setVisibleButtonIndices(newIndices);
            }
          }}
          className={classNames(
            "px-3 py-2 text-sm rounded border transition-colors duration-200 whitespace-nowrap",
            "focus:outline-none focus:ring-0 focus:border-none",
            "active:outline-none active:ring-0 active:border-none",
            "min-w-[40px] text-center"
          )}
          style={{
            backgroundColor: isDark ? seriesColor + '20' : seriesColor + '20',
            color: seriesColor,
            borderColor: "transparent",
            outline: 'none',
            boxShadow: 'none',
          }}
        >
          &lt;&lt;
        </button>
      );
    }
    
    // 現在表示されているボタンを生成
    visibleButtonIndices.forEach((buttonIndex) => {
      if (buttonIndex < totalButtons) {
        const buttonNumber = startNumber + buttonIndex * step;
        const targetDataIndex = buttonNumber - startNumber;
        const targetSlideIndex = Math.floor(targetDataIndex / 5);
        if (targetDataIndex < data.length) {
          const isCurrent = activeIndex === targetSlideIndex;
          buttons.push(
            <button
              key={buttonNumber}
              onClick={() => {
                if (swiperRef.current && swiperRef.current.swiper) {
                  swiperRef.current.swiper.slideTo(targetSlideIndex);
                }
              }}
              className={classNames(
                "px-3 py-2 text-sm rounded border transition-colors duration-200 whitespace-nowrap",
                "focus:outline-none focus:ring-1 focus:ring-black",
                "min-w-[40px] text-center"
              )}
              style={{
                backgroundColor: isCurrent ? seriesColor : (isDark ? "#333" : "#eee"),
                color: isCurrent ? "white" : (isDark ? "#fff" : "#222"),
                border: "none",
                borderColor: "transparent",
                outline: 'none',
                boxShadow: 'none',
              }}
            >
              {buttonNumber}
            </button>
          );
        }
      }
    });
    
    // >>ボタン
    const lastVisibleIndex = visibleButtonIndices[visibleButtonIndices.length - 1];
    const isNextDisabled = lastVisibleIndex >= totalButtons - 1;
    buttons.push(
      <button
        key="next"
        onClick={() => {
          if (isNextDisabled) return;
          const newIndices = [...visibleButtonIndices];
          const newLastIndex = Math.min(totalButtons - 1, newIndices[newIndices.length - 1] + 1);
          if (newLastIndex > newIndices[newIndices.length - 1]) {
            newIndices.shift();
            newIndices.push(newLastIndex);
            setVisibleButtonIndices(newIndices);
          }
        }}
        className={classNames(
          "px-3 py-2 text-sm rounded border transition-colors duration-200 whitespace-nowrap",
          "focus:outline-none focus:ring-1 focus:ring-black",
          "min-w-[40px] text-center",
          {
            "opacity-50 cursor-not-allowed": isNextDisabled,
          }
        )}
        style={{
          backgroundColor: isNextDisabled
            ? (isDark ? "#444" : "#eee")
            : (isDark ? seriesColor + '20' : seriesColor + '20'),
          color: isNextDisabled
            ? "#aaa"
            : seriesColor,
          borderColor: "transparent",
          outline: 'none',
          boxShadow: 'none',
        }}
        disabled={isNextDisabled}
      >
        &gt;&gt;
      </button>
    );
    
    return buttons;
  };

  if (!data || data.length === 0) {
    return (
      <NoInlineCard className={commonStyles.card.padding.default}>
        <div className="flex flex-col items-center justify-center h-[400px]">
          <div className={classNames(commonStyles.text.title, "text-4xl text-gray-400 dark:text-gray-600 mb-2")}>ー</div>
          <div className={classNames(commonStyles.text.subtitle, "text-gray-400 dark:text-gray-600")}>Data not found</div>
        </div>
      </NoInlineCard>
    );
  }

  // データを5個ずつのグループに分割（検索がある場合はフィルタリングされたデータを使用）
  const dataToUse = searchTerm ? filteredData : data;
  const chunkSize = 5;
  const dataChunks = [];
  for (let i = 0; i < dataToUse.length; i += chunkSize) {
    dataChunks.push(dataToUse.slice(i, i + chunkSize));
  }

  //1400以下は斜めにmodel名表示
  const isMobile = windowWidth < 1400;

  const xAxisTickProps = {
    fill: isDark ? '#FFFFFF' : '#1F2937',
    fontSize: isMobile ? 15 : 23,
    ...(isMobile && { angle: -45, textAnchor: 'end', dy: 10 }),
  };

  const chartMargin = {
    top: 20,
    right: 20,
    left: 5,
    bottom: isMobile ? 40 : 5,
  };
  
  const tableColumns = [
    { header: 'Series', accessor: 'modelSeries', cell: (item) => <p className="text-xs text-gray-500 dark:text-gray-400">{item.modelSeries && item.modelSeries !== '' ? item.modelSeries : '-'}</p> },
    { header: 'Model', accessor: 'name', cell: (item) => <p className="text-gray-900 dark:text-white">{item.name && item.name !== '' ? item.name : '-'}</p> },
    { header: 'Unit Serial', accessor: 'unitSerialNumber', cell: (item) => <p className="text-xs text-gray-500 dark:text-gray-400">{item.unitSerialNumber && item.unitSerialNumber !== '' ? item.unitSerialNumber : '-'}</p> },
    { header: 'Device ID', accessor: 'deviceId', cell: (item) => <p className="text-xs text-gray-500 dark:text-gray-400">{item.deviceId && item.deviceId !== '' ? item.deviceId : '-'}</p> },
    { header: 'Last Update', accessor: 'timestamp', cell: (item) => (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <span>{item.timestamp ? new Date(item.timestamp).toLocaleString('ja-JP') : '-'}</span>
        {item.isCanError && item.latestTimestamp && (
          <span style={{ color: 'orange' }}>
            {item.latestTimestamp ? new Date(item.latestTimestamp).toLocaleString('ja-JP') : '-' }
          </span>
        )}
      </div>
    ) },
    { header: 'Operating Time', accessor: 'hours', cell: (item) => <p>{item.hours !== undefined && item.hours !== null && item.hours !== '' ? item.hours + 'h' : '-'}</p> },
    { header: 'Working Days', accessor: 'operationDays', cell: (item) => <p>{item.operationDays !== undefined && item.operationDays !== null && item.operationDays !== '' ? item.operationDays + '日' : '-'}</p> },
  ];

  const renderMobileRow = (item) => (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      <p className="col-span-2 font-semibold text-base text-gray-900 dark:text-white">{item.name}</p>
      <p className="text-gray-500 dark:text-gray-400">Series:</p>
      <p className="text-right text-gray-800 dark:text-gray-200">{item.modelSeries || '-'}</p>
      <p className="text-gray-500 dark:text-gray-400">Unit Serial:</p>
      <p className="text-right text-gray-800 dark:text-gray-200">{item.unitSerialNumber || '-'}</p>
      <p className="text-gray-500 dark:text-gray-400">Device ID:</p>
      <p className="text-right text-gray-800 dark:text-gray-200">{item.deviceId}</p>
      <p className="text-gray-500 dark:text-gray-400">Operating Time:</p>
      <p className="text-right text-gray-800 dark:text-gray-200">{item.hours}h</p>
      <p className="text-gray-500 dark:text-gray-400">Working Days:</p>
      <p className="text-right text-gray-800 dark:text-gray-200">{item.operationDays}日</p>
      <p className="text-gray-500 dark:text-gray-400">Last Update:</p>
      <div className="text-right text-gray-800 dark:text-gray-200">
        <span>{new Date(item.timestamp).toLocaleString('ja-JP')}</span>
        {item.isCanError && item.latestTimestamp && (
          <span style={{ color: 'orange', display: 'block' }}>
            {new Date(item.latestTimestamp).toLocaleString('ja-JP')}
          </span>
        )}
      </div>
    </div>
  );

  // 検索結果が空の場合の表示
  if (searchTerm && filteredData.length === 0) {
    return (
      <Card className={commonStyles.layout.section} style={{ borderColor: seriesColor, padding: '20px' }}>
        <h2 className={classNames(commonStyles.text.title, "text-center text-lg sm:text-2xl lg:text-3xl")} style={{ color: seriesColor, marginBottom: '20px', fontWeight: '600' }}>[{selectedModel}] エンジン稼働状況</h2>
        
        {/* 検索フィールド + スキップボタン群 */}
        <div className="mb-6 w-full">
          {/* 検索フィールド - 中央寄せ */}
          <div className="flex justify-center mb-4">
            <div className="relative max-w-md w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                <HiOutlineSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classNames(
                  "block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md",
                  "placeholder-gray-500 focus:outline-none focus:placeholder-gray-400",
                  "focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                  "dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                )}
                placeholder="UNIT名で検索..."
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                  <button
                    onClick={clearSearch}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <HiOutlineX className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* スキップボタン群 - グリッド配置（検索時は非表示） */}
          <div className="flex flex-wrap justify-center gap-1 px-2 sm:gap-3 sm:px-4">
            {/* 検索時は表示しない */}
          </div>
        </div>

        <div className={classNames(
          commonStyles.grid.default,
          "md:grid-cols-5 gap-2"
        )}>
          <div className="min-w-0 md:col-span-2">
            <NoInlineCard className={commonStyles.card.padding.default}>
              <div className="flex flex-col items-center justify-center h-[450px]">
                <div className={classNames(commonStyles.text.title, "text-4xl text-gray-400 dark:text-gray-600 mb-2")}>ー</div>
                <div className={classNames(commonStyles.text.subtitle, "text-gray-400 dark:text-gray-600")}>Data not found</div>
              </div>
            </NoInlineCard>
          </div>
          <div className="pl-0 md:pl-2 md:col-span-3">
            <Table 
              columns={tableColumns} 
              data={[]}
              renderMobileRow={() => (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <p className="col-span-2 font-semibold text-base text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Series:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Unit Serial:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Device ID:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Operating Time:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Working Days:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                  <p className="text-gray-500 dark:text-gray-400">Last Update:</p>
                  <p className="text-right text-gray-400 dark:text-gray-600">ー</p>
                </div>
              )}
            />
          </div>
        </div>
      </Card>
    );
  }

  // データが5個以下の場合（または検索結果）は通常表示
  if (dataToUse.length <= 5) {
    return (
      <Card className={commonStyles.layout.section} style={{ borderColor: seriesColor, padding: '20px' }}>
        <h2 className={classNames(commonStyles.text.title, "text-center text-lg sm:text-2xl lg:text-3xl")} style={{ color: seriesColor, marginBottom: '20px', fontWeight: '600' }}>[{selectedModel}] エンジン稼働状況</h2>
        
        {/* 検索フィールド + スキップボタン群 */}
        <div className="mb-6 w-full">
          {/* 検索フィールド - 中央寄せ */}
          <div className="flex justify-center mb-4">
            <div className="relative max-w-md w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                <HiOutlineSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classNames(
                  "block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md",
                  "placeholder-gray-500 focus:outline-none focus:placeholder-gray-400",
                  "focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                  "dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                )}
                placeholder="UNIT名で検索..."
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                  <button
                    onClick={clearSearch}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <HiOutlineX className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* スキップボタン群 - 横一列配置（センター揃え） */}
          {!searchTerm && (
            <div className="flex flex-wrap justify-center gap-1 px-2 sm:gap-3 sm:px-4">
              {generateSkipButtons()}
            </div>
          )}
        </div>

        {searchTerm && (
          <div className="mb-4 text-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              検索結果: {filteredData.length}件
            </span>
          </div>
        )}

        <div className={classNames(
          commonStyles.grid.default,
          "md:grid-cols-5 gap-2"
        )}>
          <div className="min-w-0 md:col-span-2">
            <ResponsiveContainer 
              width="100%" 
              height={450} 
              className="md:h-[550px] lg:h-[650px]"
              onTouchStart={(e) => {
                // チャートエリアでのタッチイベントを優先
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // チャートエリアでのタッチ移動を優先
                e.stopPropagation();
              }}
            >
              <BarChart
                data={dataToUse}
                margin={chartMargin}
                barSize={40}
                className="md:barSize-50 lg:barSize-60"
              >
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="#374151"
                  opacity={0.1}
                />
                <XAxis 
                  dataKey="name"
                  tick={xAxisTickProps}
                  axisLine={{ stroke: '#4B5563' }}
                  padding={{ left: 30, right: 30 }}
                  className="sm:text-sm"
                  interval={isMobile ? 0 : 'preserveStartEnd'}
                  tickFormatter={(name) => {
                    const match = name && name.match(/#\d+$/);
                    return match ? match[0] : name;
                  }}
                />
                <YAxis 
                  tick={{ fill: isDark ? '#FFFFFF' : '#1F2937', fontSize: 12 }}
                  axisLine={{ stroke: '#4B5563' }}
                  label={{ 
                    value: 'Operating time (h)', 
                    angle: -90, 
                    position: 'insideLeft', 
                    fill: isDark ? '#FFFFFF' : '#1F2937',
                    fontSize: 16,
                    dx: 0,
                    dy: 70,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                  }}
                />
                <Tooltip 
                  content={<CustomTooltip />}
                  contentStyle={{
                    backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    color: isDark ? '#FFFFFF' : '#1F2937',
                    fontSize: '12px'
                  }}
                />
                <Bar 
                  dataKey="hours" 
                  fill={barColor}
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList 
                    dataKey="hours" 
                    position="top" 
                    fill={isDark ? '#FFFFFF' : '#1F2937'}
                    fontSize={12}
                    formatter={(value) => `${value}h`}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="pl-0 md:pl-2 md:col-span-3">
            <Table 
              columns={tableColumns} 
              data={dataToUse}
              renderMobileRow={renderMobileRow}
              tableColor={tableColor}
            />
          </div>
        </div>
      </Card>
    );
  }

  // データが6個以上で検索なしの場合はカルーセル表示
  return (
    <Card className={commonStyles.layout.section} style={{ borderColor: seriesColor, padding: '20px' }}>
      <h2 className={classNames(commonStyles.text.title, "text-center text-lg sm:text-2xl lg:text-3xl")} style={{ color: seriesColor, marginBottom: '20px', fontWeight: '600' }}>[{selectedModel}] エンジン稼働状況</h2>
      
             {/* 検索フィールド + スキップボタン群 */}
       <div className="mb-6 w-full">
         {/* 検索フィールド - 中央寄せ */}
         <div className="flex justify-center mb-4">
           <div className="relative max-w-md w-full">
             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
               <HiOutlineSearch className="h-5 w-5 text-gray-400" />
             </div>
             <input
               type="text"
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               className={classNames(
                 "block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md",
                 "placeholder-gray-500 focus:outline-none focus:placeholder-gray-400",
                 "focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                 "dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
               )}
               placeholder="UNIT名で検索..."
             />
             {searchTerm && (
               <div className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                 <button
                   onClick={clearSearch}
                   className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                 >
                   <HiOutlineX className="h-5 w-5" />
                 </button>
               </div>
             )}
           </div>
         </div>

         {/* スキップボタン群 - グリッド配置（センター揃え） */}
         {!searchTerm && (
           <div className="flex flex-wrap justify-center gap-1 px-2 sm:gap-3 sm:px-4">
             {generateSkipButtons()}
           </div>
         )}
       </div>
      
      <Swiper
        ref={swiperRef}
        modules={[Navigation, Pagination]}
        spaceBetween={30}
        slidesPerView={1}
        navigation={true}
        pagination={{ clickable: true }}
        className="engine-hours-swiper"
        style={{
          '--swiper-navigation-color': isDark ? '#FFFFFF' : '#1F2937',
          '--swiper-pagination-color': getPaginationColor(),
        }}
        touchRatio={0.8}
        touchAngle={45}
        touchMoveStopPropagation={false}
        preventInteractionOnTransition={true}
        resistance={true}
        resistanceRatio={0.85}
        touchStartPreventDefault={false}
        touchStartForcePreventDefault={false}
        allowTouchMove={true}
        allowSlidePrev={true}
        allowSlideNext={true}
        onTouchStart={(swiper, event) => {
          const target = event.target;
          if (target.closest('.recharts-wrapper') || target.closest('.recharts-tooltip-wrapper')) {
            event.stopPropagation();
            return false;
          }
        }}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
      >
        {dataChunks.map((chunk, index) => (
          <SwiperSlide key={index}>
            <div className={classNames(
              commonStyles.grid.default,
              "md:grid-cols-5 gap-2"
            )}>
              <div className="min-w-0 md:col-span-2">
                <ResponsiveContainer 
                  width="100%" 
                  height={450} 
                  className="md:h-[550px] lg:h-[650px]"
                  onTouchStart={(e) => {
                    // チャートエリアでのタッチイベントを優先
                    e.stopPropagation();
                  }}
                  onTouchMove={(e) => {
                    // チャートエリアでのタッチ移動を優先
                    e.stopPropagation();
                  }}
                >
                  <BarChart
                    data={chunk}
                    margin={chartMargin}
                    barSize={40}
                    className="md:barSize-50 lg:barSize-60"
                  >
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke="#374151"
                      opacity={0.1}
                    />
                    <XAxis 
                      dataKey="name"
                      tick={xAxisTickProps}
                      axisLine={{ stroke: '#4B5563' }}
                      padding={{ left: 30, right: 30 }}
                      className="sm:text-sm"
                      interval={isMobile ? 0 : 'preserveStartEnd'}
                      tickFormatter={(name) => {
                        const match = name && name.match(/#\d+$/);
                        return match ? match[0] : name;
                      }}
                    />
                    <YAxis 
                      tick={{ fill: isDark ? '#FFFFFF' : '#1F2937', fontSize: 12 }}
                      axisLine={{ stroke: '#4B5563' }}
                      label={{ 
                        value: 'Operating time (h)', 
                        angle: -90, 
                        position: 'insideLeft', 
                        fill: isDark ? '#FFFFFF' : '#1F2937',
                        fontSize: 16,
                        dx: 0,
                        dy: 70,
                        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                      }}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      contentStyle={{
                        backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        color: isDark ? '#FFFFFF' : '#1F2937',
                        fontSize: '12px'
                      }}
                    />
                    <Bar 
                      dataKey="hours" 
                      fill={barColor}
                      radius={[4, 4, 0, 0]}
                    >
                      <LabelList 
                        dataKey="hours" 
                        position="top" 
                        fill={isDark ? '#FFFFFF' : '#1F2937'}
                        fontSize={12}
                        formatter={(value) => `${value}h`}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="pl-0 md:pl-2 md:col-span-3">
                <Table 
                  columns={tableColumns} 
                  data={chunk}
                  renderMobileRow={renderMobileRow}
                  tableColor={tableColor}
                />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </Card>
  );
};

export default TimeEngineHoursBarChart;

// Swiperのカスタムスタイル
const swiperStyles = `
  .engine-hours-swiper {
    padding-bottom: 50px;
  }
  
  .engine-hours-swiper .swiper-button-next,
  .engine-hours-swiper .swiper-button-prev {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    margin-top: -20px;
  }
  
  .engine-hours-swiper .swiper-button-next:hover,
  .engine-hours-swiper .swiper-button-prev:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
  
  .engine-hours-swiper .swiper-pagination {
    bottom: 0;
  }
  
  .engine-hours-swiper .swiper-pagination-bullet {
    background-color: #888;
    opacity: 0.7;
    width: 12px;
    height: 12px;
    transition: box-shadow 0.2s;
  }
  
  .engine-hours-swiper .swiper-pagination-bullet-active {
    background-color: var(--swiper-pagination-color);
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(0,0,0,0.15);
    opacity: 1;
  }
  
  html.dark .engine-hours-swiper .swiper-pagination-bullet-active {
    box-shadow: 0 0 0 2px #222, 0 0 0 4px rgba(255,255,255,0.15);
  }
  
  /* チャートエリアでのタッチイベントを優先 */
  .engine-hours-swiper .recharts-wrapper {
    touch-action: manipulation;
    pointer-events: auto;
  }
  
  .engine-hours-swiper .recharts-tooltip-wrapper {
    touch-action: manipulation;
    pointer-events: auto;
    z-index: 1000;
  }
  
  /* Swiperのタッチイベントをチャートエリアでは無効化 */
  .engine-hours-swiper .recharts-wrapper * {
    touch-action: manipulation;
  }
`;

// スタイルを動的に追加
if (typeof document !== 'undefined') {
  // 既存のスタイルを削除
  const existingStyle = document.getElementById('engine-hours-swiper-style');
  if (existingStyle) {
    existingStyle.remove();
  }
  
  const styleElement = document.createElement('style');
  styleElement.id = 'engine-hours-swiper-style';
  styleElement.textContent = swiperStyles;
  document.head.appendChild(styleElement);
} 